import { useAgentStore } from "@/stores/agent-store";
import { useEffect, useState } from "react";
import { Icons } from "@/components/ui/icons";
import { cn } from "@/lib/cn";
import { useT } from "@/i18n";
import type en from "@/i18n/messages/input-bar-composer-tips/en";
import { inputBarComposerTipsMessages } from "@/i18n/messages/input-bar-composer-tips";

const TIP_INTERVAL_MS = 8000;
const FADE_MS = 300;

type TipKey = keyof typeof en & `tip${string}`;

/** Every tip, and whether it talks about agents (hidden when the user cannot
 *  create agents). */
const TIPS: ReadonlyArray<[TipKey, boolean]> = [
  ["tipWorkDirectory", false],
  ["tipHireAgents", true],
  ["tipManageProjects", false],
  ["tipTidy", false],
  ["tipFocus", false],
  ["tipReview", true],
  ["tipBoard", false],
  ["tipTask", false],
  ["tipNew", false],
  ["tipAgent", true],
  ["tipHeart", true],
  ["tipComment", false],
  ["tipRemember", false],
  ["tipDeliverables", true],
  ["tipGmail", false],
  ["tipNote", false],
  ["tipDelegate", true],
  ["tipPasteImages", false],
  ["tipNewLine", false],
  ["tipPin", false],
  ["tipDoubleClick", false],
  ["tipFullScreen", false],
  ["tipBoardExpand", false],
  ["tipDragTasks", false],
];

const AGENT_TIPS: TipKey[] = TIPS.map(([key]) => key);
const TASK_TIPS: TipKey[] = [
  ...TIPS.filter(([, aboutAgents]) => !aboutAgents).map(([key]) => key),
  "tipIndependentTask",
];

interface InputTipBarProps {
  visible: boolean;
}

export function InputTipBar({ visible }: InputTipBarProps) {
  const t = useT(inputBarComposerTipsMessages);
  const canCreate = useAgentStore((state) => state.specialistCreationEnabled);
  const tips = canCreate ? AGENT_TIPS : TASK_TIPS;
  const [dismissed, setDismissed] = useState(false);
  const [index, setIndex] = useState(0);
  const [faded, setFaded] = useState(true);

  // Rotate tips with fade transition
  useEffect(() => {
    if (!visible || dismissed) return;
    let fadeTimer: ReturnType<typeof setTimeout>;
    const id = setInterval(() => {
      setFaded(false);
      fadeTimer = setTimeout(() => {
        setIndex((i) => (i + 1) % tips.length);
        setFaded(true);
      }, FADE_MS);
    }, TIP_INTERVAL_MS);
    return () => {
      clearInterval(id);
      clearTimeout(fadeTimer);
    };
  }, [visible, dismissed, tips]);

  if (!visible || dismissed) return null;

  return (
    <div
      className="mb-1.5 flex items-center gap-2 px-2"
      data-testid="input-tip"
    >
      <Icons.Lightbulb className="h-3.5 w-3.5 shrink-0 text-muted" />
      <span
        aria-live="polite"
        className={cn(
          "flex-1 text-appkit-caption text-secondary transition-opacity duration-300",
          faded ? "opacity-100" : "opacity-0",
        )}
      >
        {t(tips[index % tips.length]!)}
      </span>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="shrink-0 text-muted transition-colors hover:text-primary"
        aria-label={t("dismissTips")}
        data-testid="input-tip-dismiss"
      >
        <Icons.X className="h-3 w-3" />
      </button>
    </div>
  );
}
